import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Icon,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

// API imports
import {
  fetchProjects,
  fetchIssueTypes,
  fetchAssignee,
  createIssue,
  updateIssue,
  updatePoamIssue,
  createPoamIssue,
} from "../../Service/Jira.service";
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import FormikSelectAssignee from "./formikAssigneeList";
import { getRegister } from "../../Service/RiskManagement/RiskRegister.service";
import FormikDatePicker from "../Utils/Control/FormikDatePicker";
import moment from "moment";
import { get } from "../../Service/CrudFactory";
import { notification } from "../Utils/Utils";
import { useLocation } from "react-router-dom/cjs/react-router-dom";

export default function JiraIssueDialog({
  open,
  onClose,
  initialData,
  getPOAMOptions,
  poamRow,
}) {
  const [projectList, setProjectList] = useState([]);
  const [issueTypeList, setIssueTypeList] = useState([]);
  const [assigneeList, setAssigneeList] = useState([]);
  const [loading, setLoading] = useState({
    project: false,
    issuetype: false,
    assignee: false,
    submit: false,
    risk: false,
  });
  const [falconAssignee, setFalconAssignee] = useState(null);
  const [register, setRegister] = useState([]);
  const [jiraDetails, setJiraDetails] = useState([]);
  const location = useLocation();

  const isRiskRegisterRoute = location.pathname.includes(
    "/risk-management/risk-register"
  );
  const isPoamRoute = location.pathname.includes("/poam");

  useEffect(() => {
    (async () => {
      setLoading((l) => ({ ...l, risk: true }));
      try {
        const { data } = await getRegister({
          filters: {},
          search: "",
          page_size: 100,
        });
        setRegister(data.risks);
      } catch (err) {
      } finally {
        setLoading((l) => ({ ...l, risk: false })); // ✅ always stop loader
      }
    })();
  }, []);

  const validationSchema = Yup.object({
    project: Yup.string().required("Project is required"),
    issuetype: Yup.string().required("Issue Type is required"),
    summary: Yup.string().required("Summary is required"),
    description: Yup.string().required("Description is required"),
    assignee: Yup.string().required("Assignee is required"),
    status: Yup.string().required("Status is required"),
  });

  const fetchJiraDetils = async () => {
    const response = await get(`/jira/issue/${initialData?.id || poamRow?.id}`);
    setJiraDetails(response?.data);
  };
  useEffect(() => {
    if (poamRow?.task_type === "jira" || initialData?.task_type === "jira") {
      if (initialData?.id || poamRow?.id) {
        fetchJiraDetils();
      }
    }
    setJiraDetails([]);
  }, [initialData?.id, poamRow?.id]); // run when initialData.id is available

  useEffect(() => {
    const getAssignees = async () => {
      if (jiraDetails?.project?.key) {
        try {
          const { data: assignees, status } = await fetchAssignee(
            jiraDetails.project.key
          );

          if (status) {
            setAssigneeList(
              assignees.map((a) => ({
                value: a.id,
                label: a.displayName,
                avatarUrls: a.avatarUrls,
                emailAddress: a.emailAddress,
                is_falcon_user: a.is_falcon_user,
                is_active: a.is_active,
                falcon_user_id: a.falcon_user_id,
              }))
            );
          }
        } catch (err) {}
      }
    };

    getAssignees();
  }, [jiraDetails?.project?.key]);

  const getStatusMapping = (mapping) => {
    switch (mapping) {
      case 0:
        return "To Do";
      case 1:
        return "In Progress";
      case 2:
        return "Done";

      default:
        return "";
    }
  };
  const initialValues = {
    poam_row: poamRow?.poam_row || "",
    project: jiraDetails?.project?.key || "",
    issuetype: jiraDetails?.task_type || "",
    summary: jiraDetails?.summary || "",
    description: jiraDetails?.description || "",
    // labels: initialData?.jira_details?.labels?.join(", ") || "",
    assignee: jiraDetails?.assignee?.accountId || "",
    // status: getStatusMapping(initialData?.jira_details?.status) || "To Do",
    status: jiraDetails?.status || "To Do",
    risk_id: isRiskRegisterRoute
      ? initialData?.ID || ""
      : initialData?.risk?.id || "",
    // files: "",
    duedate: jiraDetails?.due_date || "",
  };
  // get options for risks
  const getRegisterOptions = () =>
    register?.length !== 0
      ? register?.map((row) => ({
          val: row.id,
          text: row.scenario.scenario,
        }))
      : [];

  // Load project & issue type data when dialog opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading((l) => ({ ...l, project: true }));
      const { data: projects, status: projStatus } = await fetchProjects();
      if (projStatus) {
        setProjectList(projects.map((p) => ({ value: p.key, label: p.name })));
      }
      setLoading((l) => ({ ...l, project: false, issuetype: true }));

      const { data: issues, status: issueStatus } = await fetchIssueTypes();
      if (issueStatus) {
        setIssueTypeList(issues.map((i) => ({ value: i, label: i })));
      }
      setLoading((l) => ({ ...l, issuetype: false }));
    })();
  }, [open]);

  const handleProjectChange = async (projectKey) => {
    if (!projectKey) return;
    setLoading((l) => ({ ...l, assignee: true }));
    const { data: assignees, status } = await fetchAssignee(projectKey);
    if (status) {
      setAssigneeList(
        assignees.map((a) => ({
          value: a.id,
          label: a.displayName,
          avatarUrls: a.avatarUrls,
          emailAddress: a.emailAddress,
          is_falcon_user: a.is_falcon_user,
          is_active: a.is_active,
          falcon_user_id: a.falcon_user_id,
        }))
      );
    }
    setLoading((l) => ({ ...l, assignee: false }));
  };
  const handleSubmit = async (values) => {
    setLoading((l) => ({ ...l, submit: true }));

    if (isPoamRoute) {
      const payload = {
        ...values,
        duedate: values.duedate
          ? moment(values.duedate).local().format("YYYY-MM-DD")
          : null,
        // row_id: rowId,
        falcon_assignee: poamRow?.assignee?.id || falconAssignee,
        // status: "To Do",
      };
      const { risk_id, ...finalPayload } = payload;

      if (poamRow?.poam_row) {
        const { project, risk_id, poam_row, ...rest } = payload; // remove project
        const updatePayload = {
          ...rest,
          issue_key: jiraDetails?.key, // add issue_key
        };
        // return
        await updatePoamIssue(updatePayload);
        notification(
          "jira-update-success",
          "Jira Issue Successfully Updated !",
          "success"
        );
      } else {
        // return
        await createPoamIssue(finalPayload);
        notification(
          "jira-create-success",
          "Jira Issue Successfully created !",
          "success"
        );
      }
    } else {
      const payload = {
        ...values,
        duedate: values.duedate
          ? moment(values.duedate).local().format("YYYY-MM-DD")
          : null,
        // row_id: rowId,
        falcon_assignee: initialData?.assignee?.id || falconAssignee,
        // status: "To Do",
      };

      if (initialData?.id) {
        const { project, risk_id, ...rest } = payload; // remove project
        const updatePayload = {
          ...rest,
          issue_key: jiraDetails?.key, // add issue_key
        };

        // return
        await updateIssue(updatePayload);
        notification(
          "jira-update-success",
          "Jira Issue Successfully Updated !",
          "success"
        );
      } else {
        await createIssue(payload);
        notification(
          "jira-create-success",
          "Jira Issue Successfully created !",
          "success"
        );
      }
    }
    setLoading((l) => ({ ...l, submit: false }));
    // setSubmitting(false);
    onClose();
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography>
        <Typography variant="h6" style={{ fontWeight: "bold" }}>
          {initialData?.id || poamRow?.poam_row
            ? "Update Issue"
            : "Create Issue"}
        </Typography>
      </DialogTitle>

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          touched,
          errors,
          setFieldValue,
          isSubmitting,
        }) => {
          return (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  {/* Project Autocomplete */}
                  <Grid item xs={6}>
                    <Autocomplete
                      disabled={jiraDetails?.key}
                      fullWidth
                      size="small"
                      options={projectList}
                      getOptionLabel={(option) => option.label || ""}
                      loading={loading.project}
                      value={
                        projectList.find(
                          (opt) => opt.value === values.project
                        ) || null
                      }
                      onChange={(e, newValue) => {
                        setFieldValue(
                          "project",
                          newValue ? newValue.value : ""
                        );
                        handleProjectChange(newValue ? newValue.value : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Project"
                          variant="outlined"
                          error={touched.project && Boolean(errors.project)}
                          helperText={touched.project && errors.project}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading.project ? (
                                  <CircularProgress size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Issue Type Autocomplete */}
                  <Grid item xs={6}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={issueTypeList}
                      getOptionLabel={(option) => option.label || ""}
                      loading={loading.issuetype}
                      value={
                        issueTypeList.find(
                          (opt) => opt.value === values.issuetype
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        setFieldValue(
                          "issuetype",
                          newValue ? newValue.value : ""
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Issue Type"
                          variant="outlined"
                          error={touched.issuetype && Boolean(errors.issuetype)}
                          helperText={touched.issuetype && errors.issuetype}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading.issuetype ? (
                                  <CircularProgress size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Summary */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      multiline
                      maxRows={3}
                      size="small"
                      name="summary"
                      label="Summary"
                      value={values.summary}
                      onChange={handleChange}
                      error={touched.summary && Boolean(errors.summary)}
                      helperText={touched.summary && errors.summary}
                    />
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={4}
                      maxRows={10}
                      size="small"
                      name="description"
                      label="Description"
                      value={values.description}
                      onChange={handleChange}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </Grid>

                  {/* Risk */}

                  {isPoamRoute ? (
                    <>
                      <Grid item xs={12}>
                        <Autocomplete
                          // disabled={poamRow?.poam_row}
                          fullWidth
                          size="small"
                          options={getPOAMOptions || []}
                          getOptionLabel={(option) => option?.text || ""}
                          value={(() => {
                            const val =
                              getPOAMOptions.find(
                                (opt) => opt?.val === poamRow?.poam_row
                              ) || null;
                            // console.log("VAL",val)
                            // console.log("Optons",getPOAMOptions)
                            return val;
                          })()}
                          isOptionEqualToValue={(option, value) =>
                            option.val === value.val
                          }
                          onChange={(e, newValue) => {
                            setFieldValue(
                              "poam_row",
                              newValue ? newValue.val : ""
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="POAM ID" // label shown in dropdown
                              variant="outlined"
                              error={touched.poam_id && Boolean(errors.poam_id)}
                              helperText={touched.poam_id && errors.poam_id}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loading.poam ? (
                                      <CircularProgress size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          loading={loading.poam}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        disabled={isRiskRegisterRoute && initialData?.ID}
                        size="small"
                        options={getRegisterOptions() || []}
                        getOptionLabel={(option) => option?.text || ""}
                        value={(() => {
                          const val =
                            getRegisterOptions().find(
                              (opt) => opt.val === values.risk_id
                            ) || null;
                          return val;
                        })()}
                        // disabled={disabled}
                        isOptionEqualToValue={(option, value) =>
                          option.val === value.val
                        }
                        onChange={(e, newValue) => {
                          setFieldValue(
                            "risk_id",
                            newValue ? newValue.val : ""
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Risk"
                            variant="outlined"
                            error={touched.risk && Boolean(errors.risk)}
                            helperText={touched.risk && errors.risk}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loading.risk ? (
                                    <CircularProgress size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        loading={loading.risk}
                      />
                    </Grid>
                  )}

                  {/* Assignee */}
                  <Grid item xs={12}>
                    <Field
                      name="assignee"
                      label="Assignee"
                      component={FormikSelectAssignee}
                      options={assigneeList}
                      multiple={false} //for multi select
                      setFalconAssignee={setFalconAssignee}
                    />
                  </Grid>

                  <Grid item xs={8}>
                    <FormControl
                      component="fieldset"
                      error={touched.status && Boolean(errors.status)}
                    >
                      <FormLabel component="legend">Status</FormLabel>
                      <RadioGroup
                        row
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value="To Do"
                          control={<Radio />}
                          label="To Do"
                        />
                        <FormControlLabel
                          value="In Progress"
                          control={<Radio />}
                          label="In Progress"
                        />
                        <FormControlLabel
                          value="Done"
                          control={<Radio />}
                          label="Done"
                        />
                      </RadioGroup>
                      {touched.status && errors.status && (
                        <FormHelperText>{errors.status}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={4} style={{ alignSelf: "flex-end" }}>
                    <FormikDatePicker
                      name="duedate"
                      label="Due Date"
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  {/* File Upload */}
                  <Grid item xs={12}>
                    <input
                      accept="*"
                      id="file-upload"
                      multiple
                      type="file"
                      style={{ display: "none" }}
                      onChange={(event) =>
                        setFieldValue("file", event.currentTarget.files)
                      }
                    />
                    <label htmlFor="file-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<Icon>attach_file</Icon>}
                        size="small"
                      >
                        Attachment
                      </Button>
                    </label>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  disabled={loading.submit}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={loading.submit || isSubmitting}
                >
                  {loading.submit
                    ? "Submitting..."
                    : initialData?.id || poamRow?.poam_row
                    ? "Update"
                    : "Create"}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
